import {
  useState,
  useEffect,
  createContext,
  useContext,
  ReactNode,
} from "react";

export type Language =
  | "en"
  | "th"
  | "my"
  | "id"
  | "vi"
  | "ms"
  | "km"
  | "lo"
  | "bn"
  | "tl";

export const languageNames: Record<Language, string> = {
  en: "English",
  th: "ไทย",
  my: "မြန်မာ",
  id: "Indonesia",
  vi: "Tiếng Việt",
  ms: "Melayu",
  km: "ខ្មែរ",
  lo: "ລາວ",
  bn: "বাংলা",
  tl: "Filipino",
};

type TranslationValue = {
  [key in Language]: string;
};

const translations: Record<string, TranslationValue> = {
  operations: {
    en: "OPERATIONS",
    th: "การดำเนินงาน",
    my: "လုပ်ငန်းများ",
    id: "OPERASI",
    vi: "VẬN HÀNH",
    ms: "OPERASI",
    km: "ប្រតិបត្តិការ",
    lo: "ການ�ໍເຫຼັກ",
    bn: "কার্যক্রম",
    tl: "OPERATIONS",
  },
  management: {
    en: "MANAGEMENT",
    th: "การจัดการ",
    my: "စီမံခန့်ခွဲမှု",
    id: "MANAJEMEN",
    vi: "QUẢN LÝ",
    ms: "PENGURUSAN",
    km: "ការគ្រប់គ្រង",
    lo: "ການຈັດການ",
    bn: "ব্যবস্থাপনা",
    tl: "PAMamahala",
  },
  dashboard: {
    en: "Dashboard",
    th: "แดชบอร์ด",
    my: "ဒက်ရှ်ဘုတ်",
    id: "Dasbor",
    vi: "Bảng điều khiển",
    ms: "Papan pemuka",
    km: "ផ្ទាំងគ្រប់គ្រង",
    lo: "ແຜ່ນຫຼ້າ",
    bn: "ড্যাশবোর্ড",
    tl: "Dashboard",
  },
  payments: {
    en: "Payments",
    th: "การชำระเงิน",
    my: "ငွေပေးချေမှုများ",
    id: "Pembayaran",
    vi: "Thanh toán",
    ms: "Pembayaran",
    km: "ការបង់ប្រាក់",
    lo: "ການຈ່າຍ",
    bn: "পেমেন্ট",
    tl: "Pagbabayad",
  },
  ecommerce: {
    en: "E-commerce",
    th: "อีคอมเมิร์ซ",
    my: "အီလက်ထရောနစ်စျေးဝါး",
    id: "E-commerce",
    vi: "Thương mại điện tử",
    ms: "E-dagang",
    km: "អេឡិចតេស្ត",
    lo: "ອີ-คอมເມิ່ຣ",
    bn: "ই-কমার্স",
    tl: "E-commerce",
  },
  inventory: {
    en: "Inventory",
    th: "สินค้าคงคลัง",
    my: "ပစ္စည်းပါဝင်မှု",
    id: "Inventaris",
    vi: "Hàng tồn kho",
    ms: "Inventori",
    km: "ស្តុកស្តម្ភ",
    lo: "ສິນຄ້າ",
    bn: "ইনভেন্টরি",
    tl: "Imbentaryo",
  },
  tenants: {
    en: "Tenants",
    th: "ผู้เช่า",
    my: "ငှားရမ်းသူများ",
    id: "Penyewa",
    vi: "Người thuê",
    ms: "Penyewa",
    km: "អ្នកជេរ",
    lo: "ຜູ້ເຊົ່າ",
    bn: "ভাড়াটে",
    tl: "Mga nangungupahan",
  },
  analytics: {
    en: "Analytics",
    th: "การวิเคราะห์",
    my: "ခွဲခြမ်းစိတ်ဖြာမှု",
    id: "Analitik",
    vi: "Phân tích",
    ms: "Analitik",
    km: "ការវិភាគ",
    lo: "ການວິເຄາະ",
    bn: "বিশ্লেষণ",
    tl: "Analytics",
  },
  reports: {
    en: "Reports",
    th: "รายงาน",
    my: "အစီရင်ခံစာများ",
    id: "Laporan",
    vi: "Báo cáo",
    ms: "Laporan",
    km: "របាយការណ៍",
    lo: "ລາຍງານ",
    bn: "রিপোর্ট",
    tl: "Mga Ulat",
  },
  settings: {
    en: "Settings",
    th: "การตั้งค่า",
    my: "အပြင်အဆင်များ",
    id: "Pengaturan",
    vi: "Cài đặt",
    ms: "Tetapan",
    km: "ការកំណត់",
    lo: "ການຕັ້ງ",
    bn: "সেটিংস",
    tl: "Mga Setting",
  },
  logout: {
    en: "Logout",
    th: "ออกจากระบบ",
    my: "အသုံးပြန်ထွက်မှု",
    id: "Keluar",
    vi: "Đăng xuất",
    ms: "Log keluar",
    km: "ចេញ",
    lo: "ອອກ",
    bn: "লগআউট",
    tl: "Logout",
  },
  welcome: {
    en: "Welcome",
    th: "ยินดีต้อนรับ",
    my: "မင်္ဂလာပါ",
    id: "Selamat datang",
    vi: "Chào mừng",
    ms: "Selamat datang",
    km: "សូមស្វាគមន៍",
    lo: "ຍິ່ງຕ້ອນຮັບ",
    bn: "স্বাগতম",
    tl: "Maligayang pagdating",
  },
  edgeCommerce: {
    en: "Edge Commerce",
    th: "เอ็ดจ์ คอมเมิร์ซ",
    my: "အီဒ်ဂျ်ကိုမတ်စ်",
    id: "Edge Commerce",
    vi: "Edge Commerce",
    ms: "Edge Commerce",
    km: "Edge Commerce",
    lo: "Edge Commerce",
    bn: "এজ কমার্স",
    tl: "Edge Commerce",
  },
  protocolEngine: {
    en: "Protocol Engine Dashboard",
    th: "แดชบอร์ดโปรโตคอลเอนจิน",
    my: "ပြားတိုက်ဂျင်ဒက်ရှ်ဘုတ်",
    id: "Dasbor Protokol Engine",
    vi: "Bảng điều khiển Giao thức",
    ms: "Papan Pemuka Enjin Protokol",
    km: "ផ្ទាំងគ្រប់គ្រងម៉ាស៊ីនពិဒីការ",
    lo: "ແຜ່ນຫຼ້າເຄື່ອນທີ່ໂປໂຕ�ອນ",
    bn: "প্রোটোকল ইঞ্জিন ড্যাশবোর্ড",
    tl: "Dashboard ng Protocol Engine",
  },
  promptpay: {
    en: "PromptPay",
    th: "พร้อมเพย์",
    my: "ပရောမ့်ပေး",
    id: "PromptPay",
    vi: "PromptPay",
    ms: "PromptPay",
    km: "PromptPay",
    lo: "PromptPay",
    bn: "PromptPay",
    tl: "PromptPay",
  },
  wallet: {
    en: "Wallet",
    th: "กระเป๋าเงิน",
    my: "ပိုက်ဆံအိတ်",
    id: "Dompet",
    vi: "Ví",
    ms: "Dompet",
    km: "កាបូប",
    lo: "ກະເປົ໋າ",
    bn: "ওয়ালেট",
    tl: "Wallet",
  },
  affiliate: {
    en: "Affiliate",
    th: "แอฟิเลียต",
    my: "အဖွဲ့ဝင်",
    id: "Afiliasi",
    vi: "Liên kết",
    ms: "Affiliate",
    km: "ភ្ជាប់",
    lo: "ໂຮມ",
    bn: "অ্যাফিলিয়েট",
    tl: "Affiliate",
  },
  security: {
    en: "Security",
    th: "ความปลอดภัย",
    my: "လုံခြုံရေး",
    id: "Keamanan",
    vi: "Bảo mật",
    ms: "Keselamatan",
    km: "សុវត្ថិភាព",
    lo: "ຄວາມປອດໄພ",
    bn: "নিরাপত্তা",
    tl: "Seguridad",
  },
  users: {
    en: "Users Management",
    th: "การจัดการผู้ใช้",
    my: "အသုံးပြန်များ စီမံခန့်ခွဲမှု",
    id: "Manajemen Pengguna",
    vi: "Quản lý người dùng",
    ms: "Pengurusan Pengguna",
    km: "ការគ្រប់គ្រងអ្នកប្រេី",
    lo: "ການຈັດການໂມງ",
    bn: "ব্যবহারকারী ব্যবস্থাপনা",
    tl: "Pamamahala ng Mga User",
  },
  monitor: {
    en: "Monitor & Logs",
    th: "มอนิเตอร์และบันทึก",
    my: "မော်နီတိုမှုနှင့်မှတ်တမ်းများ",
    id: "Monitor & Log",
    vi: "Giám sát & Nhật ký",
    ms: "Monitor & Log",
    km: "ម៉ាស៊ីនតាមដាន & កំណត់",
    lo: "ໝາຍ & ບັນທຶກ",
    bn: "মনিটর এবং লগ",
    tl: "Monitor & Logs",
  },
  systemAdministration: {
    en: "System Administration",
    th: "การจัดการระบบ",
    my: "စနစ်စီမံခန့်ခွဲမှု",
    id: "Administrasi Sistem",
    vi: "Quản trị hệ thống",
    ms: "Pentadbiran Sistem",
    km: "ការគ្រប់គ្រងប្រព័ន្ធ",
    lo: "ການຈັດການລະບົບ",
    bn: "সিস্টেম অ্যাডমিনিস্ট্রেশন",
    tl: "Pamamahala ng Sistema",
  },
  language: {
    en: "Language",
    th: "ภาษา",
    my: "ဘာသာစကား",
    id: "Bahasa",
    vi: "Ngôn ngữ",
    ms: "Bahasa",
    km: "ភាសា",
    lo: "ພາສາ",
    bn: "ভাষা",
    tl: "Wika",
  },
  selectLanguage: {
    en: "Select Language",
    th: "เลือกภาษา",
    my: "ဘာသာစကားရွေးချယ်မှု",
    id: "Pilih Bahasa",
    vi: "Chọn ngôn ngữ",
    ms: "Pilih Bahasa",
    km: "ជ្រើសរើសភាសា",
    lo: "ເລືອກ​ພາ​ສາ",
    bn: "ভাষা নির্বাচন করুন",
    tl: "Pumili ng Wika",
  },
};

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string) => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(
  undefined,
);

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error("useLanguage must be used within a LanguageProvider");
  }
  return context;
};

interface LanguageProviderProps {
  children: ReactNode;
}

export const LanguageProvider: React.FC<LanguageProviderProps> = ({
  children,
}) => {
  const [language, setLanguage] = useState<Language>(() => {
    const saved = localStorage.getItem("language");
    return (saved as Language) || "en";
  });

  useEffect(() => {
    localStorage.setItem("language", language);
  }, [language]);

  const t = (key: string): string => {
    if (translations[key]) {
      return translations[key][language] || translations[key].en || key;
    }
    return key;
  };

  const value: LanguageContextType = {
    language,
    setLanguage,
    t,
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};
